/**
 * Client-side session diagnostics recorder (evidence channel, zero behavior).
 *
 * The Home freeze incident could not be diagnosed because a browser without
 * DevTools open keeps no console/network history. This module persists a small
 * bounded buffer of session-check failure events (per transport attempt:
 * network vs timeout vs HTTP status) and flushes them to
 * `POST /api/client-diagnostics` once any request succeeds again — the report
 * itself cannot survive the outage window, so it must wait for recovery.
 * the freeze and then navigates still delivers the evidence. localStorage
 * (not sessionStorage) keeps the buffer across a browser restart too: a phone
 * whose network dies mid-test usually ends with the browser being killed, and
 * a sessionStorage buffer would die with it — the evidence from exactly those
 * sessions is the evidence we need. The next successful load uploads it.
 *
 * Portable by design: no imports, same-origin relative URL, fire-and-forget.
 */

const STORAGE_KEY = 'deepmox.session-diagnostics';
const MAX_EVENTS = 30;

export type SessionDiagnosticOutcome = 'network' | 'timeout' | 'http';

export type SessionDiagnosticKind =
	| 'session_attempt_failed'
	| 'session_check_gave_up'
	| 'session_check_recovered'
	| 'playback_start_failed'
	| 'playback_probe_result'
	| 'media_element_error'
	| 'playback_start_skipped_expired'
	| 'playback_pause_during_start'
	| 'playback_stall_blob_recovery'
	| 'playback_progress_check'
	| 'playback_paused_handoff_cancel';

export type PlaybackVisibilityState = 'visible' | 'hidden' | 'prerender' | 'unloaded';
export type PlaybackLifecycleEvent = 'freeze' | 'resume' | 'visibilitychange' | 'pageshow' | 'pagehide' | 'focus' | 'blur';

export interface SessionDiagnosticEvent {
	readonly kind: SessionDiagnosticKind;
	/** Epoch ms when the event was recorded. */
	readonly ts: number;
	/** ms since the first buffered failure; reconstructs the real outage window. */
	readonly tOffsetMs: number;
	readonly attempt?: number;
	readonly maxAttempts?: number;
	readonly outcome?: SessionDiagnosticOutcome;
	readonly status?: number;
	readonly path?: string;
	readonly errorName?: string;
	readonly errorMessage?: string;
	readonly hydrationRound?: number;
	readonly outageMs?: number;
	readonly mediaKind?: 'audio' | 'video';
	readonly mediaErrorCode?: number;
	readonly mediaErrorMessage?: string;
	readonly networkState?: number;
	readonly readyState?: number;
	/** Signed-grant expiry minus client clock, in seconds. A fresh mount should read ~300; <=30 right after mount means the device clock is skewed forward. */
	readonly expiresSkewSeconds?: number;
	/** Duration of a signed-URL HEAD probe. */
	readonly probeMs?: number;
	/** Bounded browser stack fragment when application code pauses a pending play(). */
	readonly pauseOrigin?: string;
	/** Blob fallback evidence for a media stack stuck at HAVE_METADATA. */
	readonly recoveryPhase?: PlaybackBlobRecoveryPhase;
	readonly recoveryMs?: number;
	/** Post-recovery audibility evidence: does the audio clock actually advance? */
	readonly advancing?: boolean;
	readonly deltaMs?: number;
	readonly currentTimeSeconds?: number;
	readonly paused?: boolean;
	/** Element output state at sampling: rules a muted or zero-volume element in or out of "clock advances but silent". */
	readonly volume?: number;
	readonly muted?: boolean;
	/** Causal timing for a failed play() call; no URL, content, or account data. */
	readonly playElapsedMs?: number;
	readonly visibilityAtPlay?: PlaybackVisibilityState;
	readonly focusedAtPlay?: boolean;
	readonly lastLifecycleEventAtPlay?: PlaybackLifecycleEvent;
	readonly lastLifecycleAgeMsAtPlay?: number;
	readonly visibilityAtFailure?: PlaybackVisibilityState;
	readonly focusedAtFailure?: boolean;
	readonly lastLifecycleEventAtFailure?: PlaybackLifecycleEvent;
	readonly lastLifecycleAgeMsAtFailure?: number;
}

/** Per failed transport attempt on any GET — only failures emit, so volume stays bounded. */
export interface TransportAttemptFailure {
	readonly method: string;
	readonly path: string;
	readonly attempt: number;
	readonly maxAttempts: number;
	readonly outcome: SessionDiagnosticOutcome;
	readonly status: number;
	readonly errorName?: string;
	readonly errorMessage?: string;
	readonly durationMs: number;
}

let buffer: SessionDiagnosticEvent[] = [];
let firstFailureAt: number | null = null;
let flushInFlight = false;
let listenersArmed = false;

interface LifecycleState {
	lastEvent?: PlaybackLifecycleEvent;
	lastEventAt?: number;
	bound?: boolean;
}

export interface PlaybackLifecycleSnapshot {
	readonly visibility?: PlaybackVisibilityState;
	readonly focused?: boolean;
	readonly lastEvent?: PlaybackLifecycleEvent;
	readonly lastEventAgeMs?: number;
}

const lifecycleStates = new WeakMap<Document, LifecycleState>();

function readVisibility(doc: Document): PlaybackVisibilityState | undefined {
	const visibility = doc.visibilityState;
	return visibility === 'visible' || visibility === 'hidden' || visibility === 'prerender' || visibility === 'unloaded' ? visibility : undefined;
}

/**
 * Freeze/resume are Document lifecycle events, not Window bubbling events on
 * Chromium-family browsers. Capture them at the document so one diagnostic
 * snapshot can tell a foreground pending play apart from a page suspension.
 */
export function playbackLifecycleSnapshot(doc: Document): PlaybackLifecycleSnapshot {
	let state = lifecycleStates.get(doc);
	if (!state) {
		state = {};
		lifecycleStates.set(doc, state);
	}
	if (!state.bound) {
		state.bound = true;
		const mark = (event: Event) => {
			const type = event.type as PlaybackLifecycleEvent;
			if (type === 'freeze' || type === 'resume' || type === 'visibilitychange' || type === 'pageshow' || type === 'pagehide' || type === 'focus' || type === 'blur') {
				state!.lastEvent = type;
				state!.lastEventAt = Date.now();
			}
		};
		doc.addEventListener('freeze', mark, true);
		doc.addEventListener('resume', mark, true);
		doc.addEventListener('visibilitychange', mark, true);
		const view = doc.defaultView;
		view?.addEventListener('pageshow', mark, true);
		view?.addEventListener('pagehide', mark, true);
		view?.addEventListener('focus', mark, true);
		view?.addEventListener('blur', mark, true);
	}
	const now = Date.now();
	return {
		visibility: readVisibility(doc),
		focused: typeof doc.hasFocus === 'function' ? doc.hasFocus() : undefined,
		lastEvent: state.lastEvent,
		lastEventAgeMs: state.lastEventAt === undefined ? undefined : Math.max(0, now - state.lastEventAt),
	};
}

function storageAvailable(): boolean {
	return typeof localStorage !== 'undefined';
}

function loadPersisted(): void {
	if (!storageAvailable()) return;
	try {
		const raw = localStorage.getItem(STORAGE_KEY);
		if (!raw) return;
		const parsed: unknown = JSON.parse(raw);
		if (!Array.isArray(parsed)) return;
		buffer = parsed.filter((event): event is SessionDiagnosticEvent =>
			!!event && typeof event === 'object' && typeof (event as SessionDiagnosticEvent).kind === 'string');
		const oldestFailure = buffer.find((event) => event.kind === 'session_attempt_failed');
		firstFailureAt = oldestFailure ? oldestFailure.ts : null;
	} catch {
		// Corrupted or unavailable storage must never break hydration.
	}
}

function persist(): void {
	if (!storageAvailable()) return;
	try {
		localStorage.setItem(STORAGE_KEY, JSON.stringify(buffer.slice(-MAX_EVENTS)));
	} catch {
		// Quota/privacy failures are silently ignored — diagnostics only.
	}
}

function armRecoveryTriggers(): void {
	if (listenersArmed || typeof window === 'undefined') return;
	listenersArmed = true;
	// The flush POST fails inside the same outage that produced the events, so
	// delivery waits for the network to come back: reconnect event, a short
	// grace timer (blip already over by the time we record), or the next
	// successful session check.
	window.addEventListener('online', () => { void flushClientSessionDiagnostics(); });
	window.setTimeout(() => { void flushClientSessionDiagnostics(); }, 3_000);
}

function truncate(value: string, max: number): string {
	return value.length <= max ? value : value.slice(0, max);
}

export function recordTransportAttemptFailure(info: TransportAttemptFailure): void {
	armRecoveryTriggers();
	if (buffer.length === 0) loadPersisted();
	const now = Date.now();
	if (firstFailureAt === null) firstFailureAt = now;
	buffer.push({
		kind: 'session_attempt_failed',
		ts: now,
		tOffsetMs: now - firstFailureAt,
		attempt: info.attempt,
		maxAttempts: info.maxAttempts,
		outcome: info.outcome,
		status: info.status,
		path: truncate(info.path, 200),
		errorName: info.errorName ? truncate(info.errorName, 80) : undefined,
		errorMessage: info.errorMessage ? truncate(info.errorMessage, 300) : undefined,
	});
	if (buffer.length > MAX_EVENTS) buffer = buffer.slice(-MAX_EVENTS);
	persist();
}

/** Emitted when the Home hydration gives up on the session check (manual chip appears). */
export function recordSessionCheckGaveUp(hydrationRound: number): void {
	const now = Date.now();
	if (firstFailureAt === null) firstFailureAt = now;
	buffer.push({ kind: 'session_check_gave_up', ts: now, tOffsetMs: now - firstFailureAt, hydrationRound });
	if (buffer.length > MAX_EVENTS) buffer = buffer.slice(-MAX_EVENTS);
	persist();
}

/**
 * Emitted on the next successful session check after buffered failures, then
 * flushes everything — this is the delivery moment for evidence recorded
 * during the outage.
 */
export function recordSessionCheckRecovered(): Promise<boolean> {
	if (buffer.length === 0 && firstFailureAt === null) {
		loadPersisted();
		if (buffer.length === 0) return Promise.resolve(false);
	}
	if (buffer.some((event) => event.kind !== 'session_check_recovered')) {
		const now = Date.now();
		const outageMs = firstFailureAt !== null ? now - firstFailureAt : 0;
		buffer.push({ kind: 'session_check_recovered', ts: now, tOffsetMs: outageMs, outageMs });
	}
	persist();
	return flushClientSessionDiagnostics();
}

/** Fire-and-forget delivery of the buffered evidence. Keeps the buffer on any failure. */
export async function flushClientSessionDiagnostics(): Promise<boolean> {
	if (flushInFlight) return false;
	if (buffer.length === 0) {
		loadPersisted();
		if (buffer.length === 0) return false;
	}
	flushInFlight = true;
	try {
		const response = await fetch('/api/client-diagnostics', {
			method: 'POST',
			headers: { 'content-type': 'application/json' },
			body: JSON.stringify({ events: buffer.slice(-MAX_EVENTS) }),
			keepalive: true,
		});
		if (!response.ok) return false;
		buffer = [];
		firstFailureAt = null;
		persist();
		return true;
	} catch {
		return false;
	} finally {
		flushInFlight = false;
	}
}

/** Test/inspection access to the buffered events. */
export function peekClientSessionDiagnostics(): readonly SessionDiagnosticEvent[] {
	if (buffer.length === 0) loadPersisted();
	return buffer;
}

export interface PlaybackStartFailureInfo {
	readonly mediaKind: 'audio' | 'video';
	readonly errorName?: string;
	readonly errorMessage?: string;
	readonly mediaErrorCode?: number;
	readonly mediaErrorMessage?: string;
	readonly networkState?: number;
	readonly readyState?: number;
	readonly expiresSkewSeconds?: number;
	readonly playElapsedMs?: number;
	readonly lifecycleAtPlay?: PlaybackLifecycleSnapshot;
	readonly lifecycleAtFailure?: PlaybackLifecycleSnapshot;
}

function pushPlaybackEvent(event: Omit<SessionDiagnosticEvent, 'ts' | 'tOffsetMs' | 'kind'> & { kind: SessionDiagnosticKind }): void {
	const now = Date.now();
	if (firstFailureAt === null) firstFailureAt = now;
	buffer.push({ ts: now, tOffsetMs: now - firstFailureAt, ...event });
	if (buffer.length > MAX_EVENTS) buffer = buffer.slice(-MAX_EVENTS);
	persist();
}

/**
 * Record why an authorised play() call was rejected. The player UI presents
 * every rejection as "one more tap", which hides the real class — gesture
 * loss (NotAllowedError), source load failure (NotSupportedError + element
 * error code), or decode failure. One Android device failing forever while
 * every other device works cannot be root-caused from that UI; this event
 * carries the classification instead of guessing.
 */
export function recordPlaybackStartFailure(info: PlaybackStartFailureInfo): void {
	pushPlaybackEvent({
		kind: 'playback_start_failed',
		mediaKind: info.mediaKind,
		errorName: info.errorName ? truncate(info.errorName, 80) : undefined,
		errorMessage: info.errorMessage ? truncate(info.errorMessage, 300) : undefined,
		mediaErrorCode: info.mediaErrorCode,
		mediaErrorMessage: info.mediaErrorMessage ? truncate(info.mediaErrorMessage, 300) : undefined,
		networkState: info.networkState,
		readyState: info.readyState,
		expiresSkewSeconds: info.expiresSkewSeconds,
		playElapsedMs: info.playElapsedMs,
		visibilityAtPlay: info.lifecycleAtPlay?.visibility,
		focusedAtPlay: info.lifecycleAtPlay?.focused,
		lastLifecycleEventAtPlay: info.lifecycleAtPlay?.lastEvent,
		lastLifecycleAgeMsAtPlay: info.lifecycleAtPlay?.lastEventAgeMs,
		visibilityAtFailure: info.lifecycleAtFailure?.visibility,
		focusedAtFailure: info.lifecycleAtFailure?.focused,
		lastLifecycleEventAtFailure: info.lifecycleAtFailure?.lastEvent,
		lastLifecycleAgeMsAtFailure: info.lifecycleAtFailure?.lastEventAgeMs,
	});
	void flushClientSessionDiagnostics();
}

/** The play() caller deliberately skipped an expired source; the skew tells clock drift from genuinely stale pages apart. */
export function recordPlaybackStartSkippedExpired(info: { mediaKind: 'audio' | 'video'; expiresSkewSeconds?: number }): void {
	pushPlaybackEvent({ kind: 'playback_start_skipped_expired', mediaKind: info.mediaKind, expiresSkewSeconds: info.expiresSkewSeconds });
	void flushClientSessionDiagnostics();
}

/**
 * A pending play() rejected with AbortError only says that *some* pause won
 * the race. This records application-originated pauses while that request is
 * still pending, separating them from browser/media-engine cancellation.
 */
export function recordPlaybackPauseDuringStart(info: { mediaKind: 'audio' | 'video'; pauseOrigin?: string }): void {
	pushPlaybackEvent({
		kind: 'playback_pause_during_start',
		mediaKind: info.mediaKind,
		pauseOrigin: info.pauseOrigin ? truncate(info.pauseOrigin, 200) : undefined,
	});
	void flushClientSessionDiagnostics();
}

export type PlaybackBlobRecoveryPhase = 'engaged' | 'succeeded' | 'failed';

/**
 * Evidence for the vendor-media-stack stall fallback: after HAVE_METADATA
 * stalls on a visible page, the source is refetched as a blob through the
 * ordinary fetch stack (which demonstrably works where the element does not).
 */
export function recordPlaybackStallBlobRecovery(info: {
	mediaKind: 'audio' | 'video';
	phase: PlaybackBlobRecoveryPhase;
	recoveryMs?: number;
	errorName?: string;
	errorMessage?: string;
}): void {
	pushPlaybackEvent({
		kind: 'playback_stall_blob_recovery',
		mediaKind: info.mediaKind,
		recoveryPhase: info.phase,
		recoveryMs: info.recoveryMs,
		errorName: info.errorName ? truncate(info.errorName, 80) : undefined,
		errorMessage: info.errorMessage ? truncate(info.errorMessage, 300) : undefined,
	});
	void flushClientSessionDiagnostics();
}

/** Record that a freshly started element was paused by our own handoff-cancel guard. */
export function recordPlaybackPausedHandoffCancel(info: { mediaKind: 'audio' | 'video' }): void {
	pushPlaybackEvent({ kind: 'playback_paused_handoff_cancel', mediaKind: info.mediaKind });
	void flushClientSessionDiagnostics();
}

/**
 * A resolved play() only means the element *claims* to play. Vendor stacks can
 * reach the playing state without the audio clock ever advancing. Two
 * currentTime samples decide whether a recovered start is genuinely audible.
 */
export function recordPlaybackProgressCheck(info: {
	mediaKind: 'audio' | 'video';
	advancing: boolean;
	deltaMs?: number;
	currentTimeSeconds?: number;
	readyState?: number;
	paused?: boolean;
	volume?: number;
	muted?: boolean;
}): void {
	pushPlaybackEvent({
		kind: 'playback_progress_check',
		mediaKind: info.mediaKind,
		advancing: info.advancing,
		deltaMs: info.deltaMs,
		currentTimeSeconds: info.currentTimeSeconds,
		readyState: info.readyState,
		paused: info.paused,
		volume: info.volume,
		muted: info.muted,
	});
	void flushClientSessionDiagnostics();
}

/** A media element raised a native error event (load/decode/network) outside the play() call. */
export function recordMediaElementError(info: PlaybackStartFailureInfo): void {
	pushPlaybackEvent({
		kind: 'media_element_error',
		mediaKind: info.mediaKind,
		mediaErrorCode: info.mediaErrorCode,
		mediaErrorMessage: info.mediaErrorMessage ? truncate(info.mediaErrorMessage, 300) : undefined,
		networkState: info.networkState,
		readyState: info.readyState,
	});
	void flushClientSessionDiagnostics();
}

const probedMediaUrls = new Set<string>();

/**
 * HEAD-probe the signed media URL once with credentials. A 2xx probe next to a
 * rejected play() rules out transport/authorization and points at the media
 * element itself (decode or the browser's playback policy); a 401/403/404 or
 * network failure points at serving. At most one probe per URL: retries share
 * the same evidence question.
 */
export function probeSignedMediaUrlOnce(url: string | undefined): void {
	if (!url || typeof fetch === 'undefined') return;
	if (probedMediaUrls.has(url)) return;
	if (probedMediaUrls.size >= 16) probedMediaUrls.clear();
	probedMediaUrls.add(url);
	const startedAt = Date.now();
	fetch(url, { method: 'HEAD', credentials: 'same-origin', cache: 'no-store' })
		.then((response) => {
			pushPlaybackEvent({ kind: 'playback_probe_result', status: response.status, probeMs: Date.now() - startedAt });
			void flushClientSessionDiagnostics();
		})
		.catch((error: unknown) => {
			pushPlaybackEvent({
				kind: 'playback_probe_result',
				status: 0,
				errorName: error instanceof Error ? truncate(error.name, 80) : 'fetch_rejected',
				errorMessage: error instanceof Error ? truncate(error.message, 300) : undefined,
				probeMs: Date.now() - startedAt,
			});
			void flushClientSessionDiagnostics();
		});
}

/** Test/inspection access to the probe dedupe set. */
export function resetPlaybackDiagnosticsForTests(): void {
	probedMediaUrls.clear();
}

export function resetClientSessionDiagnosticsForTests(): void {
	buffer = [];
	firstFailureAt = null;
	flushInFlight = false;
	if (typeof localStorage !== 'undefined') {
		try { localStorage.removeItem(STORAGE_KEY); } catch { /* ignore */ }
	}
	if (typeof sessionStorage !== 'undefined') {
		try { sessionStorage.removeItem(STORAGE_KEY); } catch { /* ignore */ }
	}
}
